/* eslint-disable react/no-unknown-property */
import {
  Action,
  Card,
  CardSkeleton,
  Grid,
  ICON_ALERT_CIRCLE_OUTLINE,
  ICON_IMAGE_OUTLINE,
  Message,
  Portal,
  Spacing,
  Stack,
  Text,
} from '@doveit/bricks';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import { useSearchDesignsInfinite } from '../../hooks/use-search-designs/useSearchDesigns';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import useFilters from '../../../hooks/use-filters/useFilters';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import CanvaDesignCard from '../../components/canva-design-card/CanvaDesignCard';
import DesignExportJobSummary from '../../components/design-export-job-summary/DesignExportJobSummary';
import * as styles from './DesignListPage.style';
import { goToPage } from '../../../utils/navigate/utils';
import { CANVA_URL } from '../../constants';

const DesignListPage: React.FC = () => {
  const isMobile = useIsDevice('mobile');

  const {
    replaceFilters,
    filters,
  } = useFilters({
    query: (v) => v,
    design_id: (v) => v,
  });

  const navigate = useNavigate();

  const {
    data: designs,
    isLoading: areDesignsLoading,
    isLoadingMore: areMoreDesignsLoading,
    error: designsError,
    hasMore: hasMoreDesigns,
    loadMore: loadMoreDesigns,
  } = useSearchDesignsInfinite(filters.query, filters.design_id);

  const goToBrandTemplatesPage = React.useCallback(() => {
    navigate('/marketing/canva/brand-templates');
  }, [navigate]);

  const isTargetDesignPending = React.useMemo(() => {
    if (!filters.design_id || !designs) return false;
    return !designs.some((design) => design.id === filters.design_id);
  }, [filters.design_id, designs]);

  return (
    <>
      <SimplePageLayout>
        <Card paddingSize="M">
          {designsError && (
            <Card.Content>
              <Message
                message="Non è possibile recuperare i design Canva"
                icon={ICON_ALERT_CIRCLE_OUTLINE}
                type="critical"
                actions={[
                  <Action
                    label="Visualizza su Canva"
                    aria-label="Azione per visualizzare i design su Canva"
                    color="critical"
                    emphasis="high"
                    size="S"
                    onClick={() => goToPage(CANVA_URL)}
                  />,
                ]}
              >
                Non è possibile recuperare i design Canva da te creati.
                <br />
                Prova ad aggiornare la pagina o visualizzali su Canva.
              </Message>
            </Card.Content>
          )}

          {designs && (
            <>
              <Card.Header
                icon={{ path: ICON_IMAGE_OUTLINE }}
                title="I tuoi design Canva"
              />
              <Card.Content>
                <Stack gap={300}>
                  <Text.Body>
                    Di seguito trovi i design Canva che hai creato finora.
                  </Text.Body>

                  <SearchByQueryAction
                    variant="input"
                    width="30rem"
                    initialQuery={filters.query}
                    onSearch={(q) => replaceFilters({ query: q })}
                  />

                  {!areDesignsLoading && !areMoreDesignsLoading && !designsError && designs.length === 0 && !isTargetDesignPending && (
                    <Message
                      message="Nessun design Canva creato"
                      actions={[
                        <Action
                          label="Vedi template Canva"
                          aria-label="Azione per visualizzare i template Canva"
                          emphasis="high"
                          size="M"
                          onClick={goToBrandTemplatesPage}
                        />,
                      ]}
                    >
                      Non hai ancora creato nessun design Canva.
                      <br />
                      Per creare un nuovo design, vai sulla pagina dei template Canva e seleziona quello di tuo interesse.
                    </Message>
                  )}
                </Stack>
              </Card.Content>

              {(designs.length > 0 || isTargetDesignPending) && (
                <Card.Content>
                  <Grid gutter={150} aria-label="Lista design Canva">

                    {isTargetDesignPending && (
                      <Grid.Unit
                        key="target-design-skeleton"
                        size={{
                          XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                        }}
                      >
                        <CardSkeleton />
                      </Grid.Unit>
                    )}

                    {designs.map((design) => (
                      <Grid.Unit
                        key={design.id}
                        size={{
                          XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                        }}
                      >
                        <CanvaDesignCard design={design} />
                      </Grid.Unit>
                    ))}
                  </Grid>
                </Card.Content>
              )}
            </>
          )}

          {(areDesignsLoading || areMoreDesignsLoading) && (
            <Card.Content>
              <Spacing margin={[150, 0]}>
                <Grid gutter={150} aria-label="Design Canva in caricamento">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Grid.Unit
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      size={{
                        XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                      }}
                    >
                      <CardSkeleton />
                    </Grid.Unit>
                  ))}
                </Grid>
              </Spacing>
            </Card.Content>
          )}

          {hasMoreDesigns && (
            <Card.Content>
              <Stack alignItems="center">
                <Action
                  label="Carica altri"
                  aria-label="Carica altri design Canva"
                  onClick={loadMoreDesigns}
                  loading={areMoreDesignsLoading}
                  size={isMobile ? 'S' : 'M'}
                />
              </Stack>
            </Card.Content>
          )}
        </Card>
      </SimplePageLayout>

      <Portal>
        <div css={styles.designExportJobsSummaryWrapper}>
          <DesignExportJobSummary />
        </div>
      </Portal>
    </>
  );
};

export default DesignListPage;
